import { DesignElementVersion } from "@design-controls/types";

export const formatDesignElementVersion = (
  major?: number | null,
  minor?: number | null,
): string | undefined => {
  if (
    major === null ||
    major === undefined ||
    minor === null ||
    minor === undefined
  ) {
    return undefined;
  }

  return `${major}.${minor}`;
};

// A version in the URL is either a major.minor label ("v2.0"), which the
// version picker links to, or the internal version ("v12"), which review pages
// link to.
const VERSION_LABEL = /^v(\d+)\.(\d+)$/;

export const isDesignElementVersionLabel = (version: string): boolean =>
  VERSION_LABEL.test(version);

export const toDesignElementVersionLabel = (version: {
  version: string;
  major?: number | null;
  minor?: number | null;
}): string => {
  const formatted = formatDesignElementVersion(version.major, version.minor);
  return formatted ? `v${formatted}` : version.version;
};

export const toDesignElementVersionNumber = (label: string): string =>
  label.replace(/^v/, "");

type ComparableVersion = { major?: number | null; minor?: number | null };

export const isNewerDesignElementVersion = (
  version: ComparableVersion,
  other: ComparableVersion,
): boolean => {
  const major = version.major ?? 0;
  const otherMajor = other.major ?? 0;

  return major === otherMajor
    ? (version.minor ?? 0) > (other.minor ?? 0)
    : major > otherMajor;
};

/**
 * The version a major.minor label points at. Versions come newest first, so
 * if two ever share a label the newest wins.
 */
export const findDesignElementVersionByLabel = (
  versions: DesignElementVersion[],
  label: string,
): DesignElementVersion | undefined => {
  const match = VERSION_LABEL.exec(label);
  if (!match) {
    return undefined;
  }

  const major = Number(match[1]);
  const minor = Number(match[2]);
  return versions.find(
    (version) => version.major === major && version.minor === minor,
  );
};
